import { CrudForm } from '../shared/CrudPage';
import { accountService, enumEntries } from '@kss/api';
import type { Account, AccountCreate, AccountFilter } from '@kss/api';
import { loadAccountOptions } from '../shared/formLoaders';

/**
 * Account form — wired to the live schema verified 2026-05-05.
 *
 * Live `accountType` is an int enum: 1=Asset, 2=Liability, 3=Equity,
 * 4=Revenue, 5=Expense. Legacy form posted the string label and 400'd
 * with `UNKNOWN_FIELD:accountType` on strict-mode binding. The form
 * keeps human-readable labels and converts to int at the boundary.
 */

/* Single source of truth — see enums.ts AccountTypeLabels. */
const TYPE_BY_LABEL: Record<string, number> = Object.fromEntries(
  enumEntries('AccountType').map((e) => [e.label, e.value]),
);
const LABEL_BY_TYPE: Record<number, string> = Object.fromEntries(
  Object.entries(TYPE_BY_LABEL).map(([k, v]) => [v, k]),
);

type F = {
  code: string;
  name: string;
  accountType: string;
  description: string;
  parentAccountId: string;
  openingBalance: number;
  isActive: boolean;
};
const empty: F = {
  code: '',
  name: '',
  accountType: 'Asset',
  description: '',
  parentAccountId: '',
  openingBalance: 0,
  isActive: true,
};

export default function NewAccount() {
  return (
    <CrudForm<Account, AccountCreate, AccountFilter, F>
      title="account"
      service={accountService}
      idKey="accountId"
      basePath="/panel/accounts"
      emptyForm={empty}
      fields={[
        { key: 'code', label: 'Account code', placeholder: '1000' },
        { key: 'name', label: 'Name', required: true, placeholder: 'Cash' },
        { key: 'accountType', label: 'Type', type: 'select', options: Object.keys(TYPE_BY_LABEL).map((v) => ({ value: v, label: v })) },
        { key: 'parentAccountId', label: 'Parent account', type: 'search', loadOptions: loadAccountOptions, placeholder: 'Pick parent account' },
        { key: 'openingBalance', label: 'Opening balance (₹)', type: 'number' },
        { key: 'isActive', label: 'Active', type: 'status' },
        { key: 'description', label: 'Description', type: 'textarea', fullWidth: true },
      ]}
      fromEntity={(r) => ({
        code: r.code ?? '',
        name: r.name ?? '',
        accountType:
          typeof r.accountType === 'number'
            ? LABEL_BY_TYPE[r.accountType] ?? 'Asset'
            : String(r.accountType ?? 'Asset'),
        description: r.description ?? '',
        parentAccountId: r.parentAccountId ?? '',
        openingBalance: Number(r.openingBalance ?? 0),
        isActive: r.isActive ?? true,
      })}
      toEntity={(f, id) => ({
        accountId: id,
        code: f.code || undefined,
        name: f.name,
        accountType: TYPE_BY_LABEL[f.accountType] ?? 1,
        description: f.description || undefined,
        parentAccountId: f.parentAccountId || undefined,
        openingBalance: f.openingBalance || undefined,
        isActive: f.isActive,
      })}
      validate={(f) => (!f.name.trim() ? 'Name is required' : null)}
    />
  );
}
